import { DeploymentRecord } from '../types/DeploymentRecord';

/**
 * Parse service field to extract app and service name
 * Expected format: type:app/service (e.g., "lambda:my-app/my-service")
 */
export const parseService = (service: string): { app: string; serviceName: string } => {
  const parts = service.split(':');
  if (parts.length < 2) {
    return { app: '', serviceName: service };
  }

  const [, appService] = parts;
  const appParts = appService.split('/');

  if (appParts.length >= 2) {
    return { app: appParts[0], serviceName: appParts[1] };
  }

  return { app: appService, serviceName: '' };
};

/**
 * Extract JOB_TYPE from metadata.original_execution_input
 * Returns 'apply', 'destroy', or undefined if not available
 */
export const extractJobType = (record: any): 'apply' | 'destroy' | undefined => {
  try {
    const metadataStr = record?.metadata?.original_execution_input;
    if (!metadataStr || typeof metadataStr !== 'string') return undefined;
    const parsed = JSON.parse(metadataStr);
    const jobType = parsed?.deployment_data?.JOB_TYPE;
    if (jobType === 'destroy') return 'destroy';
    if (jobType === 'apply') return 'apply';
    return undefined;
  } catch {
    return undefined;
  }
};

// Build a ready-to-run jnlocal deploy command for an actual deployment record.
// Unlike the Jobs tab (which uses {version}/{env} placeholders), the record knows
// the concrete version and env. Returns null when required fields are missing.
export const buildDeployCommand = (record: DeploymentRecord): string | null => {
  // `service` is the canonical, required field; fall back to parsing it so
  // records carrying only `service` (not the app/job_id aliases) still work.
  const parsed = parseService(record.service || '');
  const app = record.app || record.app_name || parsed.app;
  const jobId = record.job_id || record.serviceName || parsed.serviceName;
  const env = record.envName || record.env;
  if (!record.type || !app || !jobId || !record.version || !env) return null;
  const base = `jnlocal deploy ${record.type} --app ${app} --job-id ${jobId} --version ${record.version} --env ${env}`;
  return record.type === 'adhoc' ? `${base} --async --wait` : base;
};

// Check if a service is a destroy operation
export const isDestroyOperation = (service: DeploymentRecord): boolean => {
  // Check computed jobType from metadata first
  if (service.jobType === 'destroy') return true;
  // Fall back to checking service/type for the parent "Destroy All" record
  return ['Destroy All', 'destroy'].includes(service.service || '') ||
         ['Destroy All', 'destroy'].includes(service.type || '');
};
